import {
  ONBOARDING_GLOBE,
  type OnboardingGlobe,
} from "@-/dev.improve.haniverse.data.onboarding-globe";
import onetime from "@-/com.npmjs.onetime";

const RADIANS = Math.PI / 180;
/** Graticule lines every this many degrees, sampled every `GRATICULE_STEP` degrees. */
const GRATICULE_SPACING = 30;
const GRATICULE_STEP = 6;

/**
 * Everything the globe draws, as points on the unit sphere (`[x, y, z, …]`): `x` east through
 * longitude 90°, `y` north, `z` through longitude 0° at the equator. China's rings and borders
 * also carry the Learn map's own coordinates for the same points (`chinaMap`, `…BordersMap`, map
 * units), which is what the globe morphs China into.
 */
export type OnboardingGlobeGeometry = {
  /**
   * Each line set's bounding caps on the sphere (`onboardingGlobeLineCaps`), so whatever is
   * wholly beyond the horizon is skipped without projecting it.
   */
  caps: Record<OnboardingGlobeLineSet, Float32Array>;
  china: Float32Array[];
  /** The point the globe turns to face to look at China: the middle of the Learn map. */
  chinaCenter: { latitude: number; longitude: number };
  chinaMap: Float32Array[];
  /** The borders between two cities of one province, and on the map. */
  cityBorders: Float32Array[];
  cityBordersMap: Float32Array[];
  graticule: Float32Array[];
  land: Float32Array[];
  map: { height: number; width: number };
  /**
   * The Learn map's other maps (Taiwan, Hong Kong, Macau, Singapore): their outlines, and the
   * borders between their top-level regions.
   */
  otherMapOutlines: Float32Array[];
  otherMapRegions: Float32Array[];
  /** The borders between two provinces, and on the map. */
  provinceBorders: Float32Array[];
  provinceBordersMap: Float32Array[];
};

/** The globe's sets of lines on the sphere, each with its own caps. */
export type OnboardingGlobeLineSet =
  | "china"
  | "cityBorders"
  | "graticule"
  | "land"
  | "otherMapOutlines"
  | "otherMapRegions"
  | "provinceBorders";

/**
 * Each line's bounding cap on the unit sphere, as `[x, y, z, radius]` per line: the direction of
 * its middle, and the largest angle (radians) from there to any of its points.
 */
export function onboardingGlobeLineCaps(lines: readonly Float32Array[]): Float32Array {
  const caps = new Float32Array(lines.length * 4);
  for (const [lineIndex, line] of lines.entries()) {
    const count = Math.floor(line.length / 3);
    let x = 0;
    let y = 0;
    let z = 0;
    for (let index = 0; index < count; index += 1) {
      x += line[index * 3] ?? 0;
      y += line[index * 3 + 1] ?? 0;
      z += line[index * 3 + 2] ?? 0;
    }
    const length = Math.hypot(x, y, z);
    // A line all the way round the sphere (a parallel) has no middle: it is never culled.
    let radius = Math.PI;
    if (length > 1e-6) {
      x /= length;
      y /= length;
      z /= length;
      radius = 0;
      for (let index = 0; index < count; index += 1) {
        const dot =
          x * (line[index * 3] ?? 0) +
          y * (line[index * 3 + 1] ?? 0) +
          z * (line[index * 3 + 2] ?? 0);
        radius = Math.max(radius, Math.acos(Math.min(1, Math.max(-1, dot))));
      }
    }
    caps.set([x, y, z, radius], lineIndex * 4);
  }
  return caps;
}

/** `[longitude, latitude, …]` (degrees) as points on the unit sphere. */
export function toUnitSphere(lonLat: readonly number[]): Float32Array {
  const points = new Float32Array((lonLat.length / 2) * 3);
  for (let index = 0; index + 1 < lonLat.length; index += 2) {
    const longitude = (lonLat[index] ?? 0) * RADIANS;
    const latitude = (lonLat[index + 1] ?? 0) * RADIANS;
    const cosLatitude = Math.cos(latitude);
    const offset = (index / 2) * 3;
    points[offset] = cosLatitude * Math.sin(longitude);
    points[offset + 1] = Math.sin(latitude);
    points[offset + 2] = cosLatitude * Math.cos(longitude);
  }
  return points;
}

function createGraticule(): Float32Array[] {
  const lines: Float32Array[] = [];
  for (let longitude = -180; longitude < 180; longitude += GRATICULE_SPACING) {
    const meridian: number[] = [];
    for (let latitude = -90; latitude <= 90; latitude += GRATICULE_STEP) {
      meridian.push(longitude, latitude);
    }
    lines.push(toUnitSphere(meridian));
  }
  for (let latitude = -60; latitude <= 60; latitude += GRATICULE_SPACING) {
    const parallel: number[] = [];
    for (let longitude = -180; longitude <= 180; longitude += GRATICULE_STEP) {
      parallel.push(longitude, latitude);
    }
    lines.push(toUnitSphere(parallel));
  }
  return lines;
}

export function createOnboardingGlobeGeometry(globe: OnboardingGlobe): OnboardingGlobeGeometry {
  const { borders, center, height, rings, width } = globe.mainland;
  const lines: Record<OnboardingGlobeLineSet, Float32Array[]> = {
    china: rings.map((ring) => toUnitSphere(ring.lonLat)),
    cityBorders: borders.cities.map((line) => toUnitSphere(line.lonLat)),
    graticule: createGraticule(),
    land: globe.land.map((line) => toUnitSphere(line)),
    otherMapOutlines: globe.otherMaps.outlines.map((line) => toUnitSphere(line)),
    otherMapRegions: globe.otherMaps.regions.map((line) => toUnitSphere(line)),
    provinceBorders: borders.provinces.map((line) => toUnitSphere(line.lonLat)),
  };
  const caps: Record<OnboardingGlobeLineSet, Float32Array> = {
    china: onboardingGlobeLineCaps(lines.china),
    cityBorders: onboardingGlobeLineCaps(lines.cityBorders),
    graticule: onboardingGlobeLineCaps(lines.graticule),
    land: onboardingGlobeLineCaps(lines.land),
    otherMapOutlines: onboardingGlobeLineCaps(lines.otherMapOutlines),
    otherMapRegions: onboardingGlobeLineCaps(lines.otherMapRegions),
    provinceBorders: onboardingGlobeLineCaps(lines.provinceBorders),
  };
  return {
    ...lines,
    caps,
    chinaCenter: { latitude: center[1], longitude: center[0] },
    chinaMap: rings.map((ring) => Float32Array.from(ring.map)),
    cityBordersMap: borders.cities.map((line) => Float32Array.from(line.map)),
    map: { height, width },
    provinceBordersMap: borders.provinces.map((line) => Float32Array.from(line.map)),
  };
}

/** The bundled globe, built once. */
export const getOnboardingGlobeGeometry = onetime(() =>
  createOnboardingGlobeGeometry(ONBOARDING_GLOBE),
);

/** Which way the globe faces: the longitude and latitude at its centre (degrees). */
export type OnboardingGlobeView = { latitude: number; longitude: number };

/**
 * The rotation that brings `view` to the front, as the 3×3 matrix taking a unit-sphere point to
 * screen space (`x` right, `y` down, `z` toward the viewer, all in globe radii).
 */
export function onboardingGlobeRotation({ latitude, longitude }: OnboardingGlobeView): number[] {
  "worklet";
  const lambda = longitude * RADIANS;
  const phi = latitude * RADIANS;
  const cosLambda = Math.cos(lambda);
  const sinLambda = Math.sin(lambda);
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);
  // Turn about the axis by -λ (so λ faces front), then tip by φ (so φ faces front), then flip `y`
  // to point down the screen.
  return [
    cosLambda,
    0,
    -sinLambda,
    sinPhi * sinLambda,
    -cosPhi,
    sinPhi * cosLambda,
    cosPhi * sinLambda,
    sinPhi,
    cosPhi * cosLambda,
  ];
}
